/**
 * WallPolygon Component
 *
 * Renders a single wall section polygon on the gym floor plan.
 * Supports hover states, click/tap events, and displays a label
 * with the wall section name.
 *
 * @example
 * ```tsx
 * import { WallPolygon } from '@/components/gym-map/WallPolygon';
 *
 * <WallPolygon
 *   points={[100, 100, 300, 100, 300, 200, 100, 200]}
 *   label="Main Wall"
 *   onClick={() => handleWallClick('Main Wall')}
 * />
 * ```
 */

import { useState, useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { Line, Text, Group } from 'react-konva';
import type Konva from 'konva';
import { findVisualCenter } from '@/utils/geometry';

/**
 * Color configuration for polygon styling
 */
export interface PolygonColors {
  /** Fill color for normal state */
  fill: string;
  /** Fill color for hover state */
  fillHover: string;
  /** Fill color for selected state */
  fillSelected: string;
  /** Fill color for highlight flash animation */
  fillHighlight: string;
  /** Stroke (border) color */
  stroke: string;
  /** Label text color */
  labelColor: string;
}

/**
 * Default blue color scheme
 */
const DEFAULT_COLORS: PolygonColors = {
  fill: 'rgba(59, 130, 246, 0.2)', // Blue with 20% opacity
  fillHover: 'rgba(59, 130, 246, 0.35)', // Blue with 35% opacity
  fillSelected: 'rgba(59, 130, 246, 0.5)', // Blue with 50% opacity
  fillHighlight: 'rgba(59, 130, 246, 0.7)', // Blue with 70% opacity for flash
  stroke: 'rgba(59, 130, 246, 0.8)', // Blue with 80% opacity
  labelColor: 'var(--text-color, #1e40af)', // Dark blue for label
};

/**
 * Default highlight animation duration in milliseconds
 */
const DEFAULT_HIGHLIGHT_DURATION = 200;

/**
 * Props for WallPolygon component
 */
export interface WallPolygonProps {
  /** Polygon points as flat array [x1, y1, x2, y2, ...] */
  points: number[];
  /** Wall section name to display as label */
  label: string;
  /** Whether this polygon is currently selected */
  isSelected?: boolean;
  /** Custom colors (defaults to blue theme) */
  colors?: Partial<PolygonColors>;
  /** Stroke width in pixels (default: 2) */
  strokeWidth?: number;
  /** Font size for label (default: 14) */
  fontSize?: number;
  /** Whether to show the label (default: true) */
  showLabel?: boolean;
  /** Called when polygon is clicked */
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  /** Called when polygon is tapped (touch) */
  onTap?: (event: Konva.KonvaEventObject<TouchEvent>) => void;
  /** Whether hit detection is enabled (default: true) */
  listening?: boolean;
  /** Unique identifier for the polygon */
  id?: string;
  /** Whether to enable highlight flash animation on click/tap (default: false) */
  enableHighlightAnimation?: boolean;
  /** Duration of highlight animation in milliseconds (default: 200) */
  highlightDuration?: number;
}

/**
 * Calculate the centroid (center point) of a polygon
 *
 * @param points - Flat array of polygon points [x1, y1, x2, y2, ...]
 * @returns Center point {x, y}
 */
const calculateCentroid = (points: number[]): { x: number; y: number } => {
  if (points.length < 2) {
    return { x: 0, y: 0 };
  }

  let sumX = 0;
  let sumY = 0;
  const numPoints = points.length / 2;

  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (x !== undefined && y !== undefined) {
      sumX += x;
      sumY += y;
    }
  }

  return {
    x: sumX / numPoints,
    y: sumY / numPoints,
  };
};

/**
 * Calculate the bounding box of a polygon
 *
 * @param points - Flat array of polygon points
 * @returns Bounding box { x, y, width, height }
 */
const calculateBoundingBox = (
  points: number[]
): { x: number; y: number; width: number; height: number } => {
  if (points.length < 2) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (x !== undefined && y !== undefined) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Inner component content for the polygon
 */
const WallPolygonContent = ({
  points,
  label,
  currentFill,
  colors,
  strokeWidth,
  listening,
  showLabel,
  fontSize,
  labelPosition,
  maxLabelWidth,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onTap,
}: {
  points: number[];
  label: string;
  currentFill: string;
  colors: PolygonColors;
  strokeWidth: number;
  listening: boolean;
  showLabel: boolean;
  fontSize: number;
  labelPosition: { x: number; y: number };
  maxLabelWidth: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onTap: (e: Konva.KonvaEventObject<TouchEvent>) => void;
}) => (
  <>
    <Line
      points={points}
      closed
      fill={currentFill}
      stroke={colors.stroke}
      strokeWidth={strokeWidth}
      hitStrokeWidth={10}
      listening={listening}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      onTap={onTap}
    />
    {showLabel && label && (
      <Text
        x={labelPosition.x}
        y={labelPosition.y}
        text={label}
        fontSize={fontSize}
        fill={colors.labelColor}
        align="center"
        verticalAlign="middle"
        width={maxLabelWidth}
        offsetX={maxLabelWidth / 2}
        offsetY={fontSize / 2}
        wrap="word"
        listening={false}
      />
    )}
  </>
);

/**
 * WallPolygon Component
 *
 * Renders a wall section as a closed polygon with a text label.
 * Includes hover state styling and click/tap event handling.
 * Optionally shows a highlight flash animation when clicked/tapped.
 */
const WallPolygonBase = ({
  points,
  label,
  isSelected = false,
  colors: customColors,
  strokeWidth = 2,
  fontSize = 14,
  showLabel = true,
  onClick,
  onTap,
  listening = true,
  id,
  enableHighlightAnimation = false,
  highlightDuration = DEFAULT_HIGHLIGHT_DURATION,
}: WallPolygonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Merge custom colors with defaults
  const colors = useMemo<PolygonColors>(
    () => ({
      ...DEFAULT_COLORS,
      ...customColors,
    }),
    [customColors]
  );

  // Determine current fill color based on state
  const currentFill = useMemo(() => {
    if (isHighlighted) return colors.fillHighlight;
    if (isSelected) return colors.fillSelected;
    if (isHovered) return colors.fillHover;
    return colors.fill;
  }, [isHighlighted, isSelected, isHovered, colors]);

  // Calculate label position (visual center - guaranteed to be inside the polygon)
  const labelPosition = useMemo(() => {
    // Convert flat array to Point[] for the algorithm
    const polygonPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < points.length; i += 2) {
      const x = points[i];
      const y = points[i + 1];
      if (x !== undefined && y !== undefined) {
        polygonPoints.push({ x, y });
      }
    }
    return findVisualCenter(polygonPoints);
  }, [points]);

  // Calculate bounding box for label width constraint
  const boundingBox = useMemo(() => calculateBoundingBox(points), [points]);

  /**
   * Trigger the highlight flash animation
   * Sets highlight state to true, then resets after the specified duration
   */
  const triggerHighlight = useCallback(() => {
    if (!enableHighlightAnimation) return;

    // Clear any existing timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    setIsHighlighted(true);
    highlightTimeoutRef.current = setTimeout(() => {
      setIsHighlighted(false);
      highlightTimeoutRef.current = null;
    }, highlightDuration);
  }, [enableHighlightAnimation, highlightDuration]);

  // Event handlers
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      triggerHighlight();
      onClick?.(e);
    },
    [triggerHighlight, onClick]
  );

  const handleTap = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      triggerHighlight();
      onTap?.(e);
    },
    [triggerHighlight, onTap]
  );

  // Don't render if no points
  if (points.length < 6) {
    // Need at least 3 points (6 values) for a polygon
    return null;
  }

  // Maximum width for label (80% of bounding box width)
  const maxLabelWidth = Math.max(boundingBox.width * 0.8, 50);

  const contentProps = {
    points,
    label,
    currentFill,
    colors,
    strokeWidth,
    listening,
    showLabel,
    fontSize,
    labelPosition,
    maxLabelWidth,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleClick,
    onTap: handleTap,
  };

  // Render with id only if provided (to satisfy exactOptionalPropertyTypes)
  if (id) {
    return (
      <Group id={id}>
        <WallPolygonContent {...contentProps} />
      </Group>
    );
  }

  return (
    <Group>
      <WallPolygonContent {...contentProps} />
    </Group>
  );
};

// Memoize to prevent unnecessary re-renders during map pan/zoom
export const WallPolygon = memo(WallPolygonBase);

export default WallPolygon;

// Re-export utility functions for use in other components
export { calculateCentroid, calculateBoundingBox };
